import { useDataSources } from '@/composables/useDataSources'
import {
  merge,
  DashboardComponentType,
  MeasurementFilters,
  MeasurementSource,
  ComponentFilters
} from '@/features/dashboard'
import { defineStore, storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import {
  AggregationQuery,
  Expression,
  Filter,
  GroupBy,
  Information,
  InfoValue,
  mapExpression,
  MeasurementPoint,
  Query
} from './contracts'
import { fetchAggregate, fetchInformation } from '@/services/aggregate.service'
import { FeatureType, useFilters } from '@/composables/useFilters'
import { hashGroup } from './grouping'
import { Coloring } from '../components/MeasurementCard.vue'
import { useSelectionStore } from '@/pinia/selectionNew'
import { DeepPartial, DeepPartialNull } from '@/utils/types'
import { AbsoluteRange, useTimeSelection } from '@/composables/useTimeRange'
import { EntityLevel } from '@/composables/useEntityLevel'
import { isCancel } from 'axios'
import { LevelOfDetail, useLevelOfDetail } from '@/pinia/levelOfDetail'

type MeasurementName = string
type ComponentName = string

export type ResourceType<T extends DashboardComponentType = DashboardComponentType> =
  T extends DashboardComponentType.Kpi
    ? 'value' | 'colored-entities' | 'table'
    : T extends DashboardComponentType.TimeSeries
    ? 'values'
    : T extends DashboardComponentType.Histogram
    ? 'values'
    : T extends DashboardComponentType.BarChart
    ? 'values'
    : T extends DashboardComponentType.Toplist
    ? 'values'
    : T extends DashboardComponentType.Heatmap
    ? 'values'
    : never

const resourceTypeMap: Record<DashboardComponentType, ResourceType[]> = {
  [DashboardComponentType.Kpi]: ['value', 'colored-entities', 'table'],
  [DashboardComponentType.TimeSeries]: ['values'],
  [DashboardComponentType.Histogram]: ['values'],
  [DashboardComponentType.Heatmap]: ['values'],
  [DashboardComponentType.Toplist]: ['values'],
  [DashboardComponentType.BarChart]: ['values']
}

type MeasurementRequestGroup<T extends DashboardComponentType = DashboardComponentType> = {
  type: T
  resourceType: ResourceType<T>
  suffixFilters?: string[]
  filters?: DeepPartialNull<MeasurementFilters>
  name: MeasurementName
  measurements: MeasurementSource<T>[]
}

type InformationRequestGroup = {
  filters?: DeepPartial<Query>
  name: MeasurementName
  measurements: MeasurementSource[]
}

export type DataState = 'loading' | 'loaded' | 'error' | 'empty' | 'unsupported'

function calculatePathDepth(
  path: string,
  entityLevel: EntityLevel | undefined,
  levelOfDetail: LevelOfDetail
): number {
  const depth = path.split('.').length
  return entityLevel === 'COMPLEX' || (entityLevel === 'CONSTRUCTION' && levelOfDetail === 'SPACE')
    ? depth + 1
    : depth
}

// TODO: Implement this again using a new store
// const createTagFilters = (
//   filter: FeatureFilters<FeatureType.AggregateAnalysis>
// ): Filter | undefined => {
//   if (!filter.measurementTags) return undefined
//   const ins = Object.entries(filter.measurementTags)
//     .map(
//       ([key, values]) =>
//         ({
//           type: 'in',
//           key,
//           values
//         } as Filter<'in'>)
//     )
//     .filter((f) => f.values.length > 0)
//   return ins.length > 0
//     ? {
//         type: 'and',
//         conditions: ins
//       }
//     : undefined
// }

const createQuery = (
  measurement: string,
  range: AbsoluteRange,
  path: string,
  entityLevel: EntityLevel | undefined,
  levelOfDetail: LevelOfDetail,
  tags?: string[],
  fields?: string[],
  filters?: Filter
): [number, Query] | undefined => {
  const depth = calculatePathDepth(path, entityLevel, levelOfDetail)
  return [
    depth,
    {
      measurement,
      start: range[0],
      end: range[1],
      tags,
      fields: [...new Set(fields)],
      pathQuery: path,
      filters
    }
  ]
}

const createAggregationQuery = (
  measurement: string,
  range: AbsoluteRange,
  path: string,
  group: MeasurementRequestGroup,
  entityLevel: EntityLevel | undefined,
  levelOfDetail: LevelOfDetail
): AggregationQuery | undefined => {
  const fields = group.measurements.map((x) => ({
    name: x.field.name,
    expression: mapExpression(x.field.expression, (exp) => {
      if (exp.type === 'field') {
        return {
          type: 'field',
          name: [exp.name, ...(group.suffixFilters ?? [])].join('_')
        }
      }
      return exp
    })
  }))
  const depthQuery = createQuery(
    measurement,
    range,
    path,
    entityLevel,
    levelOfDetail,
    undefined,
    fields.map((field) => field.name)
    // createTagFilters(filter) TODO: Implement this
  )
  if (!depthQuery) {
    return undefined
  }
  const [depth, query] = depthQuery
  const groupBys: GroupBy[] = [
    ...new Set([
      ...(group.filters?.query?.groupBy ?? [])
        .filter((x): x is GroupBy => x !== undefined)
        .map((x): GroupBy => {
          if (x.type === 'path_subtree' && x.depth === undefined) {
            return {
              type: 'path_subtree' as const,
              depth
            }
          }
          return x
        }),
      ...(group.resourceType === 'colored-entities'
        ? [{ type: 'path_subtree' as const, depth }]
        : [])
    ])
  ]
  return {
    query: merge(query, group.filters?.query?.query) as Query,
    aggregations: fields,
    groupBy: groupBys
  }
}

function mergeArrayDeep<T>(...a: ((T | undefined | null)[] | undefined | null)[]): T[] {
  return a.flat().filter((x) => x !== undefined) as T[]
}

export const useMeasurements = defineStore('measurements', () => {
  const measurementSources = ref<MeasurementSource[]>([])
  const filters = useFilters(FeatureType.AggregateAnalysis)
  const { setDataSource } = useDataSources()
  const componentFilterMap = ref<Record<ComponentName, ComponentFilters>>({})
  const information = ref<Information>()
  const { entityIdPathString, selectedEntityType } = storeToRefs(useSelectionStore())
  const { currentLevelOfDetail } = storeToRefs(useLevelOfDetail())
  const dataState = ref<DataState>('loading')
  let infoAbortController = new AbortController()
  let dataAbortController = new AbortController()

  const { absoluteRange } = storeToRefs(useTimeSelection())

  const onlyBusinessHours = computed(() => filters.submitted.onlyBusinessHours)
  const onlyBusinessDays = computed(() => filters.submitted.onlyBusinessDays)

  const path = computed(() => (entityIdPathString.value ? entityIdPathString.value + '.*' : '*'))

  const filteredMeasurementSources = computed(() =>
    measurementSources.value.filter((m) => componentFilterMap.value[m.componentName])
  )
  const measurementGlobalFilters = computed<DeepPartial<MeasurementFilters>>(() => ({
    suffixFilters: onlyBusinessHours.value ? ['working_hours'] : [],
    query: {
      query: {
        filters: onlyBusinessDays.value
          ? { type: 'days_of_week', days: [1, 2, 3, 4, 5] }
          : undefined
      }
    }
  }))

  const measurementGroups = computed<MeasurementRequestGroup[]>((): MeasurementRequestGroup[] =>
    hashGroup(filteredMeasurementSources.value, (m) =>
      resourceTypeMap[m.type].map((resourceType) => {
        const filters = merge<MeasurementFilters>(
          {},
          measurementGlobalFilters.value,
          m.filters?.[resourceType],
          componentFilterMap.value[m.componentName][resourceType]
        )
        return {
          type: m.type,
          name: m.measurement.name,
          resourceType,
          suffixFilters: mergeArrayDeep(
            measurementGlobalFilters.value.suffixFilters,
            m.filters?.[resourceType]?.suffixFilters,
            componentFilterMap.value[m.componentName][resourceType]?.suffixFilters
          ),
          filters: filters
        }
      })
    ).map((group) => ({
      type: group.key.type,
      name: group.key.name,
      resourceType: group.key.resourceType,
      suffixFilters: group.key.suffixFilters,
      filters: group.key.filters,
      measurements: group.values
    }))
  )

  const informationGroups = computed<InformationRequestGroup[]>((): InformationRequestGroup[] =>
    filteredMeasurementSources.value?.[0]
      ? hashGroup([filteredMeasurementSources.value[0]], (m) => [
          {
            name: m.measurement.name,
            filters: merge<Query>(
              {},
              measurementGlobalFilters.value.query?.query,
              m.filters?.['value']?.query?.query ?? undefined,
              componentFilterMap.value[m.componentName]['value']?.query?.query ?? undefined
            )
          }
        ]).map((group) => ({
          name: group.key.name,
          filters: group.key.filters,
          measurements: group.values
        }))
      : []
  )

  watch(
    [informationGroups, absoluteRange, path, selectedEntityType, currentLevelOfDetail],
    async ([groups, range, path, entityType, levelOfDetail]) => {
      if (!groups.length || !path || !range) return
      try {
        const results = await fetchInformationData(range, path, entityType, levelOfDetail, groups)
        information.value = mergeInformation(results)
      } catch (e) {
        if (isCancel(e)) {
          return
        } else {
          dataState.value = 'error'
          throw e
        }
      }
    },
    { immediate: true }
  )

  watch(
    [measurementGroups, absoluteRange, path, selectedEntityType, currentLevelOfDetail],
    async ([groups, range, path, entityType, levelOfDetail]) => {
      if (!groups.length || !path || !range) return
      dataState.value = 'loading'
      setDataSourcesLoading(groups)
      try {
        const results = await fetchData(range, path, entityType, levelOfDetail, groups)
        setDataSources(results)
        const hasData = results.some(([, data]) =>
          data.some((x) => Object.values(x.fields).some((y) => y !== undefined && y !== null))
        )
        if (hasData) {
          dataState.value = 'loaded'
        } else {
          dataState.value = 'empty'
        }
      } catch (e) {
        if (isCancel(e)) {
          return
        } else {
          dataState.value = 'error'
          throw e
        }
      }
    },
    { immediate: true }
  )

  onUnmounted(() => {
    infoAbortController.abort()
    dataAbortController.abort()
  })

  async function fetchData(
    range: AbsoluteRange,
    path: string,
    entityLevel: EntityLevel | undefined,
    levelOfDetail: LevelOfDetail,
    groups: MeasurementRequestGroup[]
  ): Promise<[MeasurementRequestGroup, MeasurementPoint[]][]> {
    dataAbortController.abort()
    dataAbortController = new AbortController()
    return (
      await Promise.all(
        groups.map(async (group) => {
          const query = createAggregationQuery(
            group.name,
            range,
            path,
            group,
            entityLevel,
            levelOfDetail
          )
          if (!query) {
            return undefined
          }
          const data = await fetchAggregate(query, dataAbortController.signal)
          return [group, data] as const
        })
      )
    ).filter((x) => x !== undefined) as [MeasurementRequestGroup, MeasurementPoint[]][]
  }

  async function fetchInformationData(
    range: AbsoluteRange,
    path: string,
    entityLevel: EntityLevel | undefined,
    levelOfDetail: LevelOfDetail,
    groups: InformationRequestGroup[]
  ): Promise<[InformationRequestGroup, MeasurementPoint[]][]> {
    infoAbortController.abort()
    infoAbortController = new AbortController()
    const fields = measurementGroups.value.flatMap((g) =>
      g.measurements.map((m) => (m.field.expression as Expression<'field'>).name)
    )

    const promises = groups.map(async (group) => {
      try {
        const depthQuery = createQuery(
          group.name,
          range,
          path,
          entityLevel,
          levelOfDetail,
          undefined,
          fields
        )
        if (!depthQuery) {
          return undefined
        }
        const [, query] = depthQuery
        const data = await fetchInformation(
          merge<Query>(query, group.filters) as Query,
          infoAbortController.signal
        )
        return [group, data] as const
      } catch (error: any) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          return undefined
        }
        throw error
      }
    })

    const results = await Promise.all(promises)

    return results.filter(
      (result): result is [InformationRequestGroup, MeasurementPoint[]] => result !== undefined
    )
  }

  function mergeInformation(
    informationGroups: [InformationRequestGroup, MeasurementPoint[]][]
  ): Information | undefined {
    const measurements = informationGroups.flatMap(([, data]) => data)
    const entityLevels: string[][] = []
    const tags: Record<string, InfoValue> = {}
    for (const measurement of measurements) {
      if (!measurement.path) continue
      Object.entries(measurement.fields).forEach(([key, value]) => {
        if (key.startsWith('tag_') && key.endsWith('_count')) {
          const tag = key.slice(4).slice(0, -6)
          if (!tags[tag]) {
            tags[tag] = {
              key: tag,
              entities: [],
              values: {}
            }
          }
          tags[tag].entities.push(measurement.path!)
          if (value) {
            const valueKey = value.toString()
            if (!tags[tag].values[valueKey]) {
              tags[tag].values[valueKey] = 0
            }
            tags[tag].values[valueKey]++
          }
        }
      })
      const path = measurement.path.split('.')
      for (let i = 0; i < path.length; i++) {
        if (!entityLevels[i]) entityLevels[i] = []
        if (!entityLevels[i].includes(path[i])) entityLevels[i].push(path[i])
      }
    }
    return {
      tags: Object.values(tags),
      entityLevels
    }
  }

  const valueMappers: {
    [T in DashboardComponentType]: {
      [R in ResourceType<T>]: (data: MeasurementPoint[], measurement: MeasurementSource) => any
    }
  } = {
    [DashboardComponentType.Kpi]: {
      value: (data, measurement) => data[0]?.fields[measurement.field.name],
      'colored-entities': (data, measurement) =>
        data
          .filter(
            (x) =>
              x.path !== undefined &&
              x.fields[measurement.field.name] !== undefined &&
              typeof x.fields[measurement.field.name] === 'number'
          )
          .map(
            (x): Coloring => ({
              id: x.path!.split('.').slice(-1)[0],
              value: x.fields[measurement.field.name]! as number
            })
          ),
      table: (data) => data
    },
    [DashboardComponentType.TimeSeries]: {
      values: (data) => data
    },
    [DashboardComponentType.Histogram]: {
      values: (data) => data
    },
    [DashboardComponentType.Heatmap]: {
      values: (data) => data
    },
    [DashboardComponentType.Toplist]: {
      values: (data) => data
    },
    [DashboardComponentType.BarChart]: {
      values: (data) => data
    }
  }

  function setDataSourcesLoading(dataGroups: MeasurementRequestGroup[]) {
    dataGroups.forEach((group) => {
      group.measurements.forEach((measurement) => {
        const id = createMeasurementId(measurement, group.resourceType)
        setDataSource({
          id,
          data: undefined,
          isLoading: true,
          error: undefined
        })
      })
    })
  }

  function setDataSources(dataGroups: [MeasurementRequestGroup, MeasurementPoint[]][]) {
    dataGroups.forEach(([group, data]) => {
      group.measurements.forEach((measurement) => {
        const id = createMeasurementId(measurement, group.resourceType)
        const value = valueMappers[group.type][group.resourceType](data, measurement)
        setDataSource({
          id,
          data: value,
          isLoading: false,
          error: undefined
        })
      })
    })
  }

  function setMeasurements(newMeasurements: MeasurementSource[]) {
    measurementSources.value = newMeasurements
  }

  function setComponentFilter(
    componentName: ComponentName,
    resourceType: ResourceType,
    filter: DeepPartial<MeasurementFilters>
  ) {
    componentFilterMap.value = {
      ...componentFilterMap.value,
      [componentName]: {
        ...componentFilterMap.value[componentName],
        [resourceType]: {
          ...componentFilterMap.value[componentName]?.[resourceType],
          ...filter
        }
      }
    }
  }

  function clearComponentFilter(componentName: ComponentName) {
    componentFilterMap.value = Object.fromEntries(
      Object.entries(componentFilterMap.value).filter(([key]) => key !== componentName)
    )
  }

  return {
    setMeasurements,
    setComponentFilter,
    clearComponentFilter,
    information,
    dataState,
    measurementGroups,
    measurementSources: filteredMeasurementSources
  }
})

export const useComponentFilterByResourceType = (
  componentName: ComponentName,
  resourceType: ResourceType,
  defaultFilter: DeepPartial<MeasurementFilters> = {}
) => {
  const filters = ref(defaultFilter)
  const { setComponentFilter, clearComponentFilter } = useMeasurements()

  watch(filters, (value) => {
    setComponentFilter(componentName, resourceType, value)
  })

  onMounted(() => {
    setComponentFilter(componentName, resourceType, filters.value)
  })

  onUnmounted(() => {
    clearComponentFilter(componentName)
  })

  return filters
}

export const useComponentFilter = (
  componentName: ComponentName,
  defaultFilter: DeepPartial<MeasurementFilters> = {}
) => {
  return useComponentFilterByResourceType(componentName, 'values', defaultFilter)
}

function createMeasurementId(measurement: MeasurementSource, resourceType: ResourceType): string {
  return [
    'measurements',
    measurement.componentName,
    measurement.measurement.name,
    measurement.field.name,
    measurement.type,
    resourceType
  ].join('.')
}
